import { OrganizationAction } from "@common/userOrganizationRoles";
import { UserType } from "@common/types/user";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useUser } from "@hooks/useUser";
import { useOverlays } from "contexts/OverlayContext";
import {
    BILLING_SETUP_REQUIRED_MESSAGE,
    EnrollAction,
} from "../organizationBilling/lib";

/** What an "Add client" control does, and whether it can be pressed. */
export type AddClientAction =
    | { isEnrollmentRequired: false; addClient: () => void }
    | {
          isEnrollmentRequired: true;
          addClient: () => void;
          isDisabled: boolean;
          /** Why the control is disabled, for its tooltip. */
          disabledReason: string | undefined;
      };

/**
 * Adds a client straight away for enrolled firms and non-regular users;
 * otherwise opens billing enrollment first, which only billing managers can do.
 */
export const useAddClientAction = (): AddClientAction => {
    const { openOverlay } = useOverlays();
    const user = useUser();
    const activeOrganization = useActiveOrganization();
    const canManageBilling = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_BILLING,
    );

    const isEnrolled = !!activeOrganization?.billing?.setupIntentId;

    if (isEnrolled || user?.type !== UserType.REGULAR) {
        return {
            isEnrollmentRequired: false,
            addClient: () => openOverlay("addClient", true),
        };
    }

    return {
        isEnrollmentRequired: true,
        addClient: () =>
            openOverlay("enrollOrganization", {
                action: EnrollAction.CREATE_CLIENT,
            }),
        isDisabled: !canManageBilling,
        disabledReason: canManageBilling
            ? undefined
            : BILLING_SETUP_REQUIRED_MESSAGE,
    };
};
